import {
  ProfileFormValues,
  IdentityFieldValues,
  GeneralFieldValues,
  getId,
  excludeEmptyFields,
  reverseProfileDocumentDataInfoFieldsMapperWithNumberOnly,
  reverseProfileDocumentDataInfoFieldsMapperWithSeriesAndNumber,
} from '@/pages/UserAccount/Profile';

import { isCountryUkraine } from '@/features/ui/ProfileChapters';

import { AddressSource, AddressType, DocumentData, DocumentTypeCode, OrderPayload } from '@/api/types';

import { BaseProfileFieldsValues } from './types';

export function buildProfileUpdatePayloadForProfile(formValues: ProfileFormValues): Partial<OrderPayload> {
  const resultAddresses = transformAddresses(formValues);

  return {
    ...(resultAddresses.length > 0 && { addresses: resultAddresses }),
    documents: transformDocument(formValues.documents, {
      passportDocTypeCode: undefined,
      registeredDocTypeCode: formValues.registeredDocTypeCode,
      actualDocTypeCode: formValues.actualDocTypeCode,
    }),
  };
}

export function buildProfileUpdatePayloadForIdentity(formValues: IdentityFieldValues): Partial<OrderPayload> {
  const isWithoutCitizenshipUnset = formValues?.isWithoutCitizenshipUnset === null;
  const withoutCitizenship = !formValues?.citizenship?.id;

  const citizenship = isWithoutCitizenshipUnset
    ? null
    : withoutCitizenship
      ? { id: null, label: null }
      : { id: formValues.citizenship?.id, label: formValues.citizenship?.label };

  return {
    citizenship,
    // @ts-ignore
    documents: transformDocument(formValues.documents, {
      passportDocTypeCode: formValues.passportDocTypeCode,
      registeredDocTypeCode: undefined,
      actualDocTypeCode: undefined,
    }),
  };
}

export function buildProfileUpdatePayloadForGeneral(formValues: GeneralFieldValues): Partial<OrderPayload> {
  return {
    date_of_birth: formValues.birthday ? formValues.birthday : undefined,
    gender: typeof formValues.gender === 'object' ? (formValues.gender?.id ?? '') : formValues.gender,
    contact_email: formValues.contactEmail,
    contact_phone: formValues.contactPhone,
  };
}

function transformAddresses(formValues: ProfileFormValues): Array<Partial<OrderPayload['addresses'][number]>> {
  const addresses: Array<Partial<OrderPayload['addresses'][number]>> = [];

  if (formValues.countryOfBirth.trim() !== '') {
    addresses.push({
      type: AddressType.BIRTH,
      country: getId(formValues.countryOfBirth),
      region: getId(formValues.regionOfBirth),
      district: getId(formValues.districtOfBirth),
      settlement: getId(formValues.cityOfBirth),
      source: AddressSource.MANUAL,
    });
  }

  let regAddress: Partial<OrderPayload['addresses'][number]> | null = null;
  if (formValues.countryOfReg) {
    regAddress = {
      type: AddressType.REGISTRATION,
      country: getId(formValues.countryOfReg),
      region: getId(formValues.regionOfReg),
      district: getId(formValues.districtOfReg),
      settlement: typeof formValues.cityOfReg === 'object' ? getId(formValues.cityOfReg) : (formValues.cityOfReg ?? ''),
      street:
        typeof formValues.streetOfReg === 'object' ? getId(formValues.streetOfReg) : (formValues.streetOfReg ?? ''),
      house: formValues.houseOfReg,
      building: formValues.buildingOfReg,
      apartment: formValues.apartmentOfReg,
      source: isCountryUkraine(formValues.countryOfReg.id) ? AddressSource.REGISTER : AddressSource.BOTH,
    };
    addresses.push(regAddress);
  }

  if (formValues.actualAddressEqualsResidence && formValues.countryOfReg) {
    if (regAddress) {
      addresses.push({
        ...regAddress,
        type: AddressType.LIVING,
        source: isCountryUkraine(formValues.countryOfReg.id) ? AddressSource.REGISTER : AddressSource.BOTH,
      });
    }
  } else if (formValues.actualCountry) {
    addresses.push({
      type: AddressType.LIVING,
      country: getId(formValues.actualCountry),
      region: getId(formValues.actualRegion),
      district: getId(formValues.actualDistrict),
      settlement:
        typeof formValues.actualCity === 'object' ? getId(formValues.actualCity) : (formValues.actualCity ?? ''),
      street:
        typeof formValues.actualStreet === 'object' ? getId(formValues.actualStreet) : (formValues.actualStreet ?? ''),
      house: formValues.actualHouse,
      building: formValues.actualBuilding,
      apartment: formValues.actualApartment,
      source: isCountryUkraine(formValues.actualCountry.id) ? AddressSource.REGISTER : AddressSource.BOTH,
    });
  }

  return addresses;
}

export function transformDocument(
  docs:
    | {
        [key: string]:
          | BaseProfileFieldsValues['documents'][keyof BaseProfileFieldsValues['documents']]
          | IdentityFieldValues['documents'][keyof IdentityFieldValues['documents']];
      }
    | undefined,
  {
    passportDocTypeCode,
    registeredDocTypeCode,
    actualDocTypeCode,
  }: {
    passportDocTypeCode?: IdentityFieldValues['documents']['passport']['type'];
    registeredDocTypeCode?: ProfileFormValues['documents']['REGISTRATION']['type'];
    actualDocTypeCode?: ProfileFormValues['documents']['LIVING']['type'];
  },
): DocumentData[] {
  const payloads: DocumentData[] = [];
  if (!docs) return payloads;

  Object.keys(docs).forEach((key, index) => {
    const doc = docs[key as keyof typeof docs];
    if (!doc || !doc.info) return;

    if (key === 'passport' && passportDocTypeCode) {
      const mapper =
        passportDocTypeCode === DocumentTypeCode.PASSPORT ||
        passportDocTypeCode === DocumentTypeCode.INTERNATIONAL_PASSPORT ||
        passportDocTypeCode === DocumentTypeCode.OLD_RESIDENCE
          ? reverseProfileDocumentDataInfoFieldsMapperWithSeriesAndNumber
          : reverseProfileDocumentDataInfoFieldsMapperWithNumberOnly;
      payloads.push({
        type: passportDocTypeCode as DocumentTypeCode,
        fields: {
          ...mapper(excludeEmptyFields(doc.info as Record<string, unknown>)),
          issueCountryIso2: 'UA',
        },
      });
      return;
    }

    if (
      key === AddressType.REGISTRATION &&
      registeredDocTypeCode === DocumentTypeCode.VITYAG_TERITORIALNOYI_GROMADI &&
      Object.keys(doc.info).length > 0
    ) {
      payloads.push({
        type: registeredDocTypeCode,
        fields: { ...excludeEmptyFields(doc.info as Record<string, unknown>) },
      });
      return;
    }

    if (key === AddressType.LIVING && actualDocTypeCode) {
      payloads.push({
        id: index,
        type: actualDocTypeCode as DocumentTypeCode,
        fields: { ...excludeEmptyFields(doc.info as Record<string, unknown>) },
      });
      return;
    }
  });

  return payloads;
}
